import React, { Fragment } from 'react';
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
  inputBaseClasses,
} from '@mui/material';
import { selectedElement, updateContentElementById } from 'features/document/documentSlice';
import { isTextEditorElement } from 'utils/typeGuards';
import { colorPalette } from 'styles/partials/colors';
import { TextOverflowRole } from 'features/document/constants';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { formatTextUpperCase } from 'utils/formatName';
import { useAppDispatch } from 'app/hooks';
import StyleCheckbox from 'components/StyleCheckbox/StyleCheckbox';

interface TextSidebarProps {
  currentElement: selectedElement;
}

export default function TextSidebar({ currentElement }: TextSidebarProps) {
  if (!isTextEditorElement(currentElement.data)) {
    return null;
  }

  const dispatch = useAppDispatch();
  const { overflowRole, maxHeight, maxTextRows, paddingX, paddingY, hideFooterOnLastPage } =
    currentElement.data.content;
  const textOverflowRoles = Object.values(TextOverflowRole);

  const handleOnChangeOverflowRole = (event: SelectChangeEvent) => {
    dispatch(
      updateContentElementById({
        content: {
          overflowRole: event.target.value,
        },
      })
    );
  };

  const handleOnChangeMaxTextRows = (maxTextRows: string) => {
    const parsedMaxTextRows = Number(maxTextRows);
    dispatch(
      updateContentElementById({
        content: {
          maxTextRows: parsedMaxTextRows < 0 ? 0 : parsedMaxTextRows,
        },
      })
    );
  };

  const handleOnChangeMaxHeight = (maxHeight: string) => {
    const parsedMaxHeight = Number(maxHeight);
    dispatch(
      updateContentElementById({
        content: {
          maxHeight: parsedMaxHeight < 0 ? 0 : parsedMaxHeight,
        },
      })
    );
  };

  const handleOnChangePadding = (paddingX?: number, paddingY?: number) => {
    dispatch(
      updateContentElementById({
        content: {
          paddingX: paddingX == undefined ? 12 : paddingX < 0 ? 0 : paddingX,
          paddingY: paddingY == undefined ? 8 : paddingY < 0 ? 0 : paddingY,
        },
      })
    );
  };

  const handleOnChangeHideFooterOnLastPage = (hideFooterOnLastPage: boolean) => {
    dispatch(
      updateContentElementById({
        content: {
          hideFooterOnLastPage,
        },
      })
    );
  };

  return (
    <Grid container direction='row' className='container' gap={2}>
      <Grid item xs={12}>
        <Typography color={colorPalette.darkGray1} fontSize={'16px'} fontWeight={500} marginBottom={'32px'}>
          Text Editor Block
        </Typography>
        <Box display={'flex'} flexDirection={'column'} gap={'24px'}>
          <Box>
            <Typography fontSize={'14px'} fontWeight={500} marginBottom={'16px'}>
              Padding X
            </Typography>
            <TextField
              fullWidth
              type='number'
              value={paddingX == undefined ? 12 : paddingX}
              sx={{
                [`& .${inputBaseClasses.root}`]: {
                  borderRadius: '8px',
                  height: '48px',
                },
              }}
              onChange={e => handleOnChangePadding(Number(e.target.value), paddingY)}
            />
          </Box>
          <Box>
            <Typography fontSize={'14px'} fontWeight={500} marginBottom={'16px'}>
              Padding Y
            </Typography>
            <TextField
              fullWidth
              type='number'
              value={paddingY == undefined ? 8 : paddingY}
              sx={{
                [`& .${inputBaseClasses.root}`]: {
                  borderRadius: '8px',
                  height: '48px',
                },
              }}
              onChange={e => handleOnChangePadding(paddingX, Number(e.target.value))}
            />
          </Box>
          <Box alignItems={'center'} columnGap={'8px'} display={'flex'}>
            <Typography color={colorPalette.darkGray1} fontSize={'14px'} fontWeight={500}>
              Role On Page Overflow
            </Typography>
            <Tooltip
              title='This role only applies when the "Hide Header & Footer On Overflow" option of the current page is active.'
              arrow
            >
              <InfoRoundedIcon color='primary' fontSize='small' />
            </Tooltip>
          </Box>
          <FormControl fullWidth>
            <InputLabel id={`role-select`}>Role</InputLabel>
            <Select
              labelId={`role-select`}
              label='Role'
              id={`role-select`}
              sx={{ height: '48px' }}
              value={overflowRole}
              onChange={handleOnChangeOverflowRole}
            >
              {textOverflowRoles.map((value, index) => (
                <MenuItem
                  key={index}
                  value={value}
                  sx={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px' }}
                >
                  {formatTextUpperCase(value)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {overflowRole === TextOverflowRole.CONTENT && (
            <Box>
              <Typography display={'flex'} columnGap={'8px'} fontSize={'14px'} fontWeight={400} marginBottom={'16px'}>
                Max Text Rows
                <Tooltip title='Number of text rows until page break.' arrow>
                  <InfoRoundedIcon color='primary' fontSize='small' />
                </Tooltip>
              </Typography>
              <TextField
                fullWidth
                type='number'
                value={maxTextRows || 0}
                sx={{
                  [`& .${inputBaseClasses.root}`]: {
                    borderRadius: '8px',
                    height: '48px',
                  },
                }}
                onChange={e => handleOnChangeMaxTextRows(e.target.value)}
              />
            </Box>
          )}
          {overflowRole === TextOverflowRole.FOOTER && (
            <Fragment>
              <Box>
                <Typography display={'flex'} columnGap={'8px'} fontSize={'14px'} fontWeight={400} marginBottom={'16px'}>
                  Max Height
                </Typography>
                <TextField
                  fullWidth
                  type='number'
                  value={maxHeight || 0}
                  sx={{
                    [`& .${inputBaseClasses.root}`]: {
                      borderRadius: '8px',
                      height: '48px',
                    },
                  }}
                  onChange={e => handleOnChangeMaxHeight(e.target.value)}
                />
              </Box>
              <StyleCheckbox
                option={{ label: 'Hide Footer On Last Page', value: '50px' }}
                handleChange={() => handleOnChangeHideFooterOnLastPage(!hideFooterOnLastPage)}
                checked={Boolean(hideFooterOnLastPage)}
              />
            </Fragment>
          )}
          {overflowRole === TextOverflowRole.HEADER && (
            <Box>
              <Typography display={'flex'} columnGap={'8px'} fontSize={'14px'} fontWeight={400} marginBottom={'16px'}>
                Max Height
                <Tooltip
                  title='This property determines the maximum height of the element, in pixels, in case of overflow.'
                  arrow
                >
                  <InfoRoundedIcon color='primary' fontSize='small' />
                </Tooltip>
              </Typography>
              <TextField
                fullWidth
                type='number'
                value={maxHeight || 0}
                sx={{
                  [`& .${inputBaseClasses.root}`]: {
                    borderRadius: '8px',
                    height: '48px',
                  },
                }}
                onChange={e => handleOnChangeMaxHeight(e.target.value)}
              />
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
}
